/* RESPONSIBLE TEAM: team-phone */
import Model, { attr } from '@ember-data/model';
import type { FinVoiceVoiceLanguage } from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import type { FinVoiceVoice } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import type { FinVoiceRunbook } from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';

export type FinVoiceNumberConfigurationPredicates = {
  /**
   * @deprecated Use ScheduledPercentageRollout instead
   */
  percentageRollout?: number;
  /**
   * @deprecated Use ScheduledPercentageRollout instead
   */
  officeHoursComparison?: 'in' | 'nin';
  officeHoursSchedule?: string;
} & ScheduledPercentageRollout;

export interface HandoverToVoicemailConfiguration {
  type: 'handover-to-voicemail';
  config: {
    assigneeId?: string;
  };
}

export type ScheduledPercentageRollout = Partial<
  Record<ScheduledConfigurationKey, { percentageRollout?: number }>
>;

export interface HandoverToHumanConfiguration {
  type: 'handover-to-human';
  config: {
    assigneeId?: string;
    deflectionTimer?: number; // in seconds
    goodbyeMessage?: string;
  };
}

export interface DeflectEscalationConfiguration {
  type: 'deflect-escalation';
  config: {
    callDuringOfficeHours?: boolean;
    schedule?: string;
    offerEmail?: boolean;
    emailAddress?: string;
    offerChat?: boolean;
    chatUrl?: string;
  };
}

export interface HandoverToWorkflowConfiguration {
  type: 'handover-to-workflow';
  config: {
    workflowId?: string;
    goodbyeMessage?: string;
  };
}

export interface HandoverToForwardCallConfiguration {
  type: 'handover-to-forward-call';
  config: {
    forwardToNumber?: string;
    username?: string;
    password?: string;
    assigneeId?: string;
    closeConversationOnForward?: boolean;
    goodbyeMessage?: string;
  };
}

export interface HandoverToResumeIvrWorkflowConfiguration {
  type: 'handover-resume-ivr-workflow';
  config: {
    ivrLocale?: FinVoiceVoiceLanguage;
  };
}

export interface BaseHandoverConfiguration {
  autoCloseWhenNotEscalated?: boolean;
  defaultTeamId?: string;
  attributes?: string[];
  askForCsatRating?: boolean;
  csatTtsLanguage?: string;
  procedureId?: string;
}

export type HandoverConfiguration =
  | HandoverToVoicemailConfiguration
  | HandoverToHumanConfiguration
  | DeflectEscalationConfiguration
  | HandoverToWorkflowConfiguration
  | HandoverToForwardCallConfiguration
  | HandoverToResumeIvrWorkflowConfiguration;

export type ScheduledConfigurationKey = 'default' | 'in' | 'nin';
export type HandoverConfigurationTools =
  | 'handover-to-voicemail'
  | 'handover-to-human'
  | 'deflect-escalation'
  | 'handover-to-workflow'
  | 'handover-to-forward-call'
  | 'handover-resume-ivr-workflow';

export type FinVoiceNumberConfigurationHandoverConfiguration = BaseHandoverConfiguration &
  Partial<Record<ScheduledConfigurationKey, HandoverConfiguration>>;

export default class FinVoiceNumberConfiguration extends Model {
  @attr('string', { allowNull: false }) declare appId: string;
  @attr('string', { allowNull: true }) declare workspacePhoneNumber: string;
  @attr('string') declare workspacePhoneNumberId: string;
  @attr() declare predicates: FinVoiceNumberConfigurationPredicates;
  @attr('string') declare voice: FinVoiceVoice;
  @attr('string') declare greeting: string;
  @attr('string') declare greetingOutOfOfficeHours?: string;
  @attr('string') declare runbook?: FinVoiceRunbook;
  @attr() declare classificationAttributes?: string[];
  @attr() declare handoverConfiguration: FinVoiceNumberConfigurationHandoverConfiguration;
  @attr('string') declare status: string;
  @attr('string') declare csatMessage: string;
  @attr('string') declare workspacePhoneNumberType: 'ivr' | 'sip' | 'phone';

  get ivrLanguage(): FinVoiceVoiceLanguage | undefined {
    if (this.workspacePhoneNumberType !== 'ivr') {
      return undefined;
    }

    if (this.handoverConfiguration.in?.type === 'handover-resume-ivr-workflow') {
      return this.handoverConfiguration.in.config?.ivrLocale;
    }

    if (this.handoverConfiguration.nin?.type === 'handover-resume-ivr-workflow') {
      return this.handoverConfiguration.nin.config?.ivrLocale;
    }

    return undefined;
  }

  set ivrLanguage(language: FinVoiceVoiceLanguage) {
    if (this.workspacePhoneNumberType !== 'ivr') {
      return;
    }

    if (this.handoverConfiguration.in?.type === 'handover-resume-ivr-workflow') {
      if (!this.handoverConfiguration.in.config) {
        this.handoverConfiguration.in.config = { ivrLocale: language };
      } else {
        this.handoverConfiguration.in.config.ivrLocale = language;
      }
    }

    if (this.handoverConfiguration.nin?.type === 'handover-resume-ivr-workflow') {
      if (!this.handoverConfiguration.nin.config) {
        this.handoverConfiguration.nin.config = { ivrLocale: language };
      } else {
        this.handoverConfiguration.nin.config.ivrLocale = language;
      }
    }
  }

  get attributes(): string[] {
    return this.classificationAttributes || [];
  }

  get isValidConfiguration(): boolean {
    return (
      !!this.appId &&
      !!this.workspacePhoneNumber &&
      !!this.predicates &&
      !!this.handoverConfiguration
    );
  }
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'fin-voice/number-configuration': FinVoiceNumberConfiguration;
  }
}
